import api from "./api";
import type { PanelLevel } from "../config/panelCatalog";

export interface PanelPermission {
  visible: boolean;
  level: PanelLevel;
}

export interface AccessProfile {
  id: string;
  name: string;
  description?: string | null;
  permissions: Record<string, PanelPermission>;
  is_active?: boolean;
  branchID?: string | null;
  assigned_count?: number;
}

export interface AccessMember {
  id: string;
  name: string;
  employeeID?: string | null;
  email?: string | null;
  role?: string | null;
  profile_photo_url?: string | null;
}

export interface MyPermissions {
  has_access: boolean;
  access_id: string | null;
  access_name: string | null;
  permissions: Record<string, PanelPermission>;
  role: string | null;
}

export const listAccess = async (): Promise<AccessProfile[]> => {
  const res = await api.get("/access");
  const list: AccessProfile[] = Array.isArray(res.data) ? res.data : [];
  // Defensive de-dup by name: a multi-worker seeding race could create several
  // default profiles with the same name (e.g. "Intern"); show each once.
  const seen = new Set<string>();
  return list.filter((a) => {
    const key = (a.name || a.id || "").trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const getAccess = async (id: string): Promise<AccessProfile> => {
  const res = await api.get(`/access/${id}`);
  return res.data;
};

export const createAccess = async (
  body: { name: string; description?: string; permissions: Record<string, PanelPermission> }
): Promise<AccessProfile> => {
  const res = await api.post("/access", body);
  return res.data;
};

export const updateAccess = async (
  id: string,
  body: { name?: string; description?: string; permissions?: Record<string, PanelPermission>; is_active?: boolean }
): Promise<AccessProfile> => {
  const res = await api.put(`/access/${id}`, body);
  return res.data;
};

export const deleteAccess = async (id: string): Promise<void> => {
  await api.delete(`/access/${id}`);
};

export const getMyPermissions = async (): Promise<MyPermissions> => {
  const res = await api.get("/access/my-permissions");
  return res.data;
};

export const getAccessMembers = async (id: string): Promise<AccessMember[]> => {
  const res = await api.get(`/access/${id}/members`);
  return res.data;
};

export const getEmployeeAccess = async (
  employeeId: string
): Promise<{ employee_id: string; access_id: string | null }> => {
  const res = await api.get(`/access/employee/${employeeId}`);
  return res.data;
};

export const assignEmployeeAccess = async (
  employeeId: string,
  accessId: string | null
): Promise<{ employee_id: string; access_id: string | null }> => {
  const res = await api.put(`/access/employee/${employeeId}`, { access_id: accessId });
  return res.data;
};
